import { createApiRef, createRouteRef, useApi, configApiRef, createPlugin, createApiFactory, discoveryApiRef, fetchApiRef, createRoutableExtension, createComponentExtension } from '@backstage/core-plugin-api';
import { AwsApiClient, MissingResources } from '@aws/aws-core-plugin-for-backstage-react';
import React, { useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useEntity, MissingAnnotationEmptyState } from '@backstage/plugin-catalog-react';
import { generateShortcutLink, getOneOfEntityAnnotations } from '@aws/aws-core-plugin-for-backstage-common';
import { AWS_CODEPIPELINE_ARN_ANNOTATION, AWS_CODEPIPELINE_TAGS_ANNOTATION, AWS_CODEPIPELINE_ARN_ANNOTATION_LEGACY } from '@aws/aws-codepipeline-plugin-for-backstage-common';
import { LinearProgress, Box, Select, Link, Typography } from '@material-ui/core';
import { StatusAborted, StatusOK, StatusError, StatusRunning, ResponseErrorPanel, Table, SubvalueCell } from '@backstage/core-components';
import { StageExecutionStatus } from '@aws-sdk/client-codepipeline';
import humanizeDuration from 'humanize-duration';
import { useAsyncRetry } from 'react-use';
import { getCompoundEntityRef } from '@backstage/catalog-model';
import { parse } from '@aws-sdk/util-arn-parser';

const awsCodePipelineApiRef = createApiRef({
  id: "plugin.aws-codepipeline.service"
});

class AwsCodePipelineApiClient extends AwsApiClient {
  constructor(options) {
    super({
      backendName: "aws-codepipeline",
      ...options
    });
  }
  async getPipelineExecutionsByEntity({
    entity
  }) {
    const urlSegment = `v1/entity/${encodeURIComponent(
      entity.namespace
    )}/${encodeURIComponent(entity.kind)}/${encodeURIComponent(
      entity.name
    )}/executions`;
    const services = await this.get(urlSegment);
    return services;
  }
  async getPipelineStateByEntity({
    entity
  }) {
    const urlSegment = `v1/entity/${encodeURIComponent(
      entity.namespace
    )}/${encodeURIComponent(entity.kind)}/${encodeURIComponent(
      entity.name
    )}/state`;
    const services = await this.get(urlSegment);
    return services;
  }
}

const rootRouteRef = createRouteRef({
  id: "aws-codepipeline"
});

const PipelineStageStatus = ({
  status
}) => {
  switch (status) {
    case StageExecutionStatus.InProgress:
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StatusRunning, null), " In progress");
    case StageExecutionStatus.Failed:
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StatusError, null), " Failed");
    case StageExecutionStatus.Cancelled:
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StatusAborted, null), " Cancelled");
    case StageExecutionStatus.Stopped:
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StatusAborted, null), " Stopped");
    case StageExecutionStatus.Stopping:
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StatusAborted, null), " Stopping");
    case StageExecutionStatus.Succeeded:
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StatusOK, null), " Succeeded");
    default:
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StatusAborted, null), " Unknown");
  }
};

function formatTime(date) {
  if (date) {
    const difference = (/* @__PURE__ */ new Date()).getTime() - new Date(date).getTime();
    return humanizeDuration(difference, {
      largest: 1
    });
  }
  return "-";
}

function usePipelineExecutions({ entity }) {
  const awsCodePipelineApi = useApi(awsCodePipelineApiRef);
  const {
    loading,
    value: response,
    error,
    retry
  } = useAsyncRetry(async () => {
    return await awsCodePipelineApi.getPipelineExecutionsByEntity({
      entity: getCompoundEntityRef(entity)
    });
  }, []);
  return { loading, response, error, retry };
}

const renderTrigger = (row) => {
  var _a;
  if (row.sourceRevisions === void 0) {
    return /* @__PURE__ */ React.createElement(Typography, { variant: "body2", noWrap: true }, "-");
  }
  let commitMessage = "";
  if (row.sourceRevisions.length > 0) {
    const sourceRevision = row.sourceRevisions[0];
    if (sourceRevision.revisionSummary) {
      switch (sourceRevision.actionName) {
        case "SourceAction":
          commitMessage = sourceRevision.revisionSummary || "";
          break;
        case "Source":
          commitMessage = sourceRevision.revisionSummary || "";
          break;
        case "Checkout": {
          const summary = JSON.parse(sourceRevision.revisionSummary || "{}");
          commitMessage = summary.CommitMessage;
          break;
        }
      }
    }
    const subvalue = /* @__PURE__ */ React.createElement(React.Fragment, null, ((_a = sourceRevision.revisionId) == null ? void 0 : _a.substring(0, 6)) || "", " - ", commitMessage);
    return /* @__PURE__ */ React.createElement(SubvalueCell, { value: sourceRevision.actionName, subvalue });
  }
  return "-";
};
const generatedColumns = (pipelineName, region, accountId, ssoSubdomain) => {
  return [
    {
      title: "Execution",
      field: "Pipeline",
      render: (row) => {
        if (row.pipelineExecutionId) {
          const projectUrl = `https://${region}.console.aws.amazon.com/codesuite/codepipeline/pipelines/${pipelineName}/executions/${row.pipelineExecutionId}/timeline?region=${region}`;
          return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
            Link,
            {
              href: ssoSubdomain ? generateShortcutLink(ssoSubdomain, accountId, projectUrl) : projectUrl,
              target: "_blank"
            },
            row.pipelineExecutionId
          ));
        }
        return /* @__PURE__ */ React.createElement(React.Fragment, null);
      }
    },
    {
      title: "Last Run",
      field: "",
      render: (row) => `${formatTime(row.lastUpdateTime)} ago`
    },
    {
      title: "Status",
      field: "status",
      render: (row) => {
        if (row.status) {
          return /* @__PURE__ */ React.createElement(Box, { display: "flex", alignItems: "center" }, /* @__PURE__ */ React.createElement(PipelineStageStatus, { status: row == null ? void 0 : row.status }));
        }
        return /* @__PURE__ */ React.createElement(React.Fragment, null);
      }
    },
    {
      title: "Source Revision",
      field: "revisions",
      render: (row) => renderTrigger(row)
    }
  ];
};
const CodePipelineExecutionsTable = ({
  response
}) => {
  const configApi = useApi(configApiRef);
  const ssoSubdomain = configApi.getOptionalString("aws.sso.subdomain");
  const { accountId } = parse(response.pipelineArn);
  return /* @__PURE__ */ React.createElement(
    Table,
    {
      data: response.pipelineExecutions,
      columns: generatedColumns(
        response.pipelineName,
        response.pipelineRegion,
        accountId,
        ssoSubdomain
      ),
      title: "AWS CodePipeline"
    }
  );
};
const CodePipelineMultipleExecutionsContent = ({
  response
}) => {
  const [selected, setSelected] = useState(
    response.pipelineExecutions[0]
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Box, { marginBottom: 2 }, /* @__PURE__ */ React.createElement(
    Select,
    {
      "data-testid": "select-pipeline",
      value: selected.pipelineArn,
      onChange: (e) => setSelected(
        response.pipelineExecutions.find(
          (p) => p.pipelineArn === e.target.value
        )
      )
    },
    response.pipelineExecutions.map((execution) => /* @__PURE__ */ React.createElement("option", { key: execution.pipelineArn, value: execution.pipelineArn }, execution.pipelineName))
  )), /* @__PURE__ */ React.createElement(CodePipelineExecutionsTable, { response: selected }));
};
const CodePipelineExecutionsContent = ({
  response
}) => {
  if ((response == null ? void 0 : response.pipelineExecutions.length) === 0) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(MissingResources, null));
  }
  if (response.pipelineExecutions.length === 1) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      CodePipelineExecutionsTable,
      {
        response: response.pipelineExecutions[0]
      }
    ));
  }
  return /* @__PURE__ */ React.createElement(CodePipelineMultipleExecutionsContent, { response });
};
const CodePipelineExecutions = ({
  entity
}) => {
  const { response, loading, error } = usePipelineExecutions({ entity });
  if (error) {
    return /* @__PURE__ */ React.createElement(ResponseErrorPanel, { error });
  }
  if (loading) {
    return /* @__PURE__ */ React.createElement(LinearProgress, null);
  }
  return /* @__PURE__ */ React.createElement(CodePipelineExecutionsContent, { response });
};

const isAwsCodePipelineAvailable = (entity) => getOneOfEntityAnnotations(entity, [
  AWS_CODEPIPELINE_ARN_ANNOTATION,
  AWS_CODEPIPELINE_TAGS_ANNOTATION,
  AWS_CODEPIPELINE_ARN_ANNOTATION_LEGACY
]) !== void 0;
const Router = () => {
  const { entity } = useEntity();
  if (!isAwsCodePipelineAvailable(entity)) {
    return /* @__PURE__ */ React.createElement(
      MissingAnnotationEmptyState,
      {
        annotation: [
          AWS_CODEPIPELINE_ARN_ANNOTATION,
          AWS_CODEPIPELINE_TAGS_ANNOTATION
        ]
      }
    );
  }
  return /* @__PURE__ */ React.createElement(Routes, null, /* @__PURE__ */ React.createElement(Route, { path: "/", element: /* @__PURE__ */ React.createElement(CodePipelineExecutions, { entity }) }));
};

var Router$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Router: Router,
  isAwsCodePipelineAvailable: isAwsCodePipelineAvailable
});

const awsCodePipelinePlugin = createPlugin({
  id: "aws-codepipeline",
  routes: {
    root: rootRouteRef
  },
  apis: [
    createApiFactory({
      api: awsCodePipelineApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: ({ discoveryApi, fetchApi }) => new AwsCodePipelineApiClient({ discoveryApi, fetchApi })
    })
  ]
});
const EntityAwsCodePipelineExecutionsContent = awsCodePipelinePlugin.provide(
  createRoutableExtension({
    name: "EntityAwsCodePipelineExecutionsContent",
    component: () => Promise.resolve().then(function () { return Router$1; }).then((m) => m.Router),
    mountPoint: rootRouteRef
  })
);
const EntityAwsCodePipelineCard = awsCodePipelinePlugin.provide(
  createComponentExtension({
    name: "EntityAwsCodePipelineCard",
    component: {
      lazy: () => import('./index-CkdrTMON.esm.js').then(
        (m) => m.CodePipelineStateCard
      )
    }
  })
);

export { EntityAwsCodePipelineExecutionsContent as E, PipelineStageStatus as P, awsCodePipelineApiRef as a, awsCodePipelinePlugin as b, EntityAwsCodePipelineCard as c, isAwsCodePipelineAvailable as i };
//# sourceMappingURL=index-CDlfRIbg.esm.js.map
